import type { TTSwapRequest, TTChainParticipant } from "./api";

export function isSwapPending(request: { status: string }): boolean {
  return (
    request.status === "PENDING_COUNTERPART" ||
    request.status === "PENDING_ADMIN"
  );
}

export type SwapApprovalState = {
  chain_participants?: TTChainParticipant[] | null;
  status: string;
  neis_only?: boolean;
  requester_user_id: number;
  source_user_id?: number | null;
  counterpart_user_id?: number | null;
  source_approved_at?: string | null;
  target_approved_at?: string | null;
};

export function swapNeedsMyAction(
  request: SwapApprovalState,
  userId: number | null,
  isAdmin: boolean,
): boolean {
  if (userId === null) return false;
  if (isAdmin && isSwapPending(request)) return true;
  return (
    request.status === "PENDING_COUNTERPART" &&
    (request.chain_participants
      ? request.chain_participants.some(
          (p) => p.user_id === userId && !p.approved_at,
        )
      : (request.source_user_id === userId && !request.source_approved_at) ||
        (request.counterpart_user_id === userId && !request.target_approved_at))
  );
}

export function isCoverRequest(
  request?: { kind?: string | null } | null,
): boolean {
  return request?.kind === "COVER";
}

export function swapNextStep(
  request: SwapApprovalState & { kind?: string | null },
): string {
  const cover = isCoverRequest(request);
  if (request.status === "REVERTED")
    return cover
      ? "승인 취소 · 대강 전 담당 교사로 복원되었습니다. 승인과 복원 이력은 보존됩니다."
      : "승인 취소 · 교체 전 시간표로 복원되었습니다. 승인과 복원 이력은 보존됩니다.";
  if (request.status === "APPROVED")
    return request.neis_only
      ? "나이스 변경 완료 · 실제 운영 수업은 유지됩니다."
      : cover
        ? "대강 완료 · 과목은 그대로이고 담당 교사가 변경되었습니다."
        : "교체 완료 · 주간 시간표에 반영되었습니다.";
  if (request.status === "CANCELLED")
    return "요청 취소 · 시간표는 변경되지 않았습니다.";
  if (request.status === "REJECTED")
    return "요청 거절 · 대화로 조율한 뒤 새로 요청할 수 있습니다.";
  if (request.status === "PENDING_ADMIN")
    return cover
      ? "다음 단계: 관리자가 최종 확인하면 대강이 반영됩니다."
      : "다음 단계: 관리자가 최종 확인하면 시간표에 반영됩니다.";
  return cover
    ? "대강 교사의 동의를 기다립니다. 관리자는 별도로 소통한 뒤 먼저 승인할 수 있습니다."
    : "담당 교사의 동의를 기다립니다. 관리자는 별도로 소통한 뒤 먼저 승인할 수 있습니다.";
}

export type SwapLesson = {
  id: number;
  date: string;
  period: number;
  status: string;
  classroom_id: number;
  teacher_id: number;
  place_id?: number | null;
};

export type SwapCandidateOptions = {
  isAdmin: boolean;
  myTeacherIds: ReadonlySet<number>;
  pendingLessonIds?: ReadonlySet<number>;
};

export function swapSourceIssue(
  source: SwapLesson,
  options?: SwapCandidateOptions,
): string | null {
  if (source.status !== "SCHEDULED") return "휴강한 수업은 교체할 수 없습니다.";
  if (options && !options.isAdmin && options.myTeacherIds.size === 0)
    return "내 계정에 연결된 담당 수업이 없습니다. 시간표 관리자에게 교사 계정 연결을 요청해 주세요.";
  if (options?.pendingLessonIds?.has(source.id))
    return "진행 중인 교체·대강 요청이 있는 수업입니다. 요청함에서 기존 요청을 처리하거나 취소해 주세요.";
  return null;
}

// Match the server: keep the class fixed, move each teacher/subject/place together.
// Keep rejection reasons beside the candidates so the UI never guesses a conflict.
export function getSwapCandidates(
  lessons: SwapLesson[],
  sourceId: number | null,
  options?: SwapCandidateOptions,
) {
  const candidateIds = new Set<number>();
  const recommendedIds = new Set<number>();
  const blockedReasons = new Map<number, string>();
  const scheduled = lessons.filter((lesson) => lesson.status === "SCHEDULED");
  const source = lessons.find((lesson) => lesson.id === sourceId);
  const sourceIssue = source ? swapSourceIssue(source, options) : null;
  if (!source || sourceIssue)
    return { candidateIds, recommendedIds, blockedReasons, sourceIssue };
  const occupied = new Map<string, SwapLesson[]>();
  for (const lesson of scheduled) {
    const key = `${lesson.date}/${lesson.period}`;
    const slot = occupied.get(key) ?? [];
    slot.push(lesson);
    occupied.set(key, slot);
  }
  const conflict = (
    slot: SwapLesson,
    incoming: SwapLesson,
    target: SwapLesson,
  ) => {
    const others = (occupied.get(`${slot.date}/${slot.period}`) ?? []).filter(
      (other) => other.id !== source.id && other.id !== target.id,
    );
    const when = `${slot.date} ${slot.period}교시`;
    if (others.some((other) => other.teacher_id === incoming.teacher_id))
      return `${when}에 담당 교사의 다른 수업이 있습니다.`;
    if (others.some((other) => other.classroom_id === slot.classroom_id))
      return `${when}에 해당 학급의 다른 수업이 있습니다.`;
    if (
      incoming.place_id != null &&
      others.some((other) => other.place_id === incoming.place_id)
    )
      return `${when}에 특별실을 사용하는 다른 수업이 있습니다.`;
    return null;
  };
  for (const target of lessons) {
    if (target.id === source.id) continue;
    const reason =
      swapSourceIssue(target, options) ??
      (target.classroom_id !== source.classroom_id
        ? "같은 학급의 수업끼리 교체해 주세요."
        : null) ??
      (target.date === source.date && target.period === source.period
        ? "같은 시간의 수업은 교체할 수 없습니다."
        : null) ??
      (options &&
      !options.isAdmin &&
      !options.myTeacherIds.has(source.teacher_id) &&
      !options.myTeacherIds.has(target.teacher_id)
        ? "내 담당 수업이 포함된 교체만 요청할 수 있습니다."
        : null) ??
      conflict(target, source, target) ??
      conflict(source, target, target);
    if (reason) blockedReasons.set(target.id, reason);
    else {
      candidateIds.add(target.id);
      // Same-teacher moves may be valid, but are not useful swap suggestions.
      if (target.teacher_id !== source.teacher_id)
        recommendedIds.add(target.id);
    }
  }
  return { candidateIds, recommendedIds, blockedReasons, sourceIssue };
}

export function swapCandidateIds(
  lessons: SwapLesson[],
  sourceId: number | null,
): Set<number> {
  return getSwapCandidates(lessons, sourceId).candidateIds;
}

export function swapLessonIds(
  request: Pick<
    TTSwapRequest,
    "chain_moves" | "source_weekly_id" | "target_weekly_id" | "kind"
  >,
): number[] {
  if (isCoverRequest(request)) return [request.source_weekly_id];
  return [
    ...new Set(
      request.chain_moves?.map((move) => move.from_id) ?? [
        request.source_weekly_id,
        request.target_weekly_id,
      ],
    ),
  ];
}

export function swapInvolvesUser(
  request: TTSwapRequest,
  userId: number | null,
): boolean {
  return (
    userId !== null &&
    (request.requester_user_id === userId ||
      request.source_user_id === userId ||
      request.counterpart_user_id === userId ||
      !!request.participant_user_ids?.includes(userId))
  );
}

export function swapHasUnconfirmedTeachers(request: TTSwapRequest): boolean {
  return request.chain_participants
    ? request.chain_participants.some((p) => !p.approved_at)
    : !request.source_approved_at || !request.target_approved_at;
}
